import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconArrowDownBlueCircle from "../../../../img/icons/arrow-down-blue-circle.svg";
import { ConfiguratorPrice } from "../../../common/ConfiguratorPrice";
import { ErrorModal } from "../../../common/ErrorModal";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { ConfiguratorTypes, STR_NO, STR_YES } from "../../../constants";
import {
    IConcreteBundle,
    IOptionValues,
    IProduct,
    IProductCategory,
} from "../../../models/catalogue";
import { IWebPageURL } from "../../../models/nominals";
import { IAPIPrice } from "../../../models/prices";
import { DiscountDisplayMode } from "../constants";
import { ConfiguratorPriceLoading } from "../elements/ConfiguratorPriceLoading";
import { isPreorderProduct } from "../utils";

import styles from "./StickyConfigurator.module.scss";

const onCloseErrorModal = (
    onClose: () => void,
    e: React.FormEvent<Element> | React.KeyboardEvent<Element>,
): void => {
    e.preventDefault();
    onClose();
};

const onAddToBasketClick = (
    rootProduct: IProduct | null,
    onAddToBasket: () => void,
    e: React.FormEvent<HTMLElement>,
): void => {
    e.preventDefault();
    if (!rootProduct) {
        console.error(
            "[Configurator] Cannot check for upsell when no product is loaded.",
        );
        return;
    }
    onAddToBasket();
};

const buildPrice = (
    price: IAPIPrice | null,
    upgradedVariant: IProduct | null,
    financingLink: IWebPageURL | null | undefined,
    strikeThroughMSRP: boolean,
    actualPriceStyle: string,
    overrideFinancingCopy: string,
): React.JSX.Element => {
    if (!price || !upgradedVariant) {
        return (
            <ConfiguratorPriceLoading
                configuratorType={ConfiguratorTypes.PANEL}
            />
        );
    }

    return (
        <div
            className={`${styles.priceContainer} sticky-configurator__price-container`}
        >
            <ConfiguratorPrice
                price={price}
                variant={upgradedVariant}
                selectedAddonPrice={null}
                financingLink={financingLink ? financingLink : null}
                strikeThroughMSRP={strikeThroughMSRP}
                discountDisplayMode={DiscountDisplayMode.DISABLED}
                actualPriceStyle={actualPriceStyle}
                overrideFinancingCopy={overrideFinancingCopy}
            />
        </div>
    );
};

const buildCopy = (
    rootProduct: IProduct | null,
    selectedCategories: IProductCategory[] | undefined,
    optionValues: IOptionValues,
    isPLCVersion: boolean,
    areOptionsExpanded: boolean,
    onExpandOptions: () => void,
    price: IAPIPrice | null,
    upgradedVariant: IProduct | null,
    financingLink: IWebPageURL | null | undefined,
    strikeThroughMSRP: boolean,
    actualPriceStyle: string,
    overrideFinancingCopy: string,
): React.JSX.Element | undefined => {
    if (!rootProduct) {
        return;
    }
    // Build category text
    let categoryOptions;
    if (selectedCategories) {
        categoryOptions = selectedCategories.map((code) => {
            const prefixName = code.name
                .split(" ")
                .map((word) => {
                    return word[0].toUpperCase() + word.substring(1);
                })
                .join(" ");

            const prefixNameCapped = isPLCVersion && (
                <span className="sticky-configurator__option-prefix">{`${prefixName}: `}</span>
            );

            return (
                <div className="sticky-configurator__option" key={code.name}>
                    {prefixNameCapped}
                    {code.value}
                </div>
            );
        });
    }

    // Build option text
    const optionCodes = rootProduct.attributes.product_options?.value || [];
    const productOptions = optionCodes.map((code) => {
        let value = optionValues[code];
        if (value === STR_NO || typeof value === "undefined") {
            return;
        }
        if (value === STR_YES) {
            // TODO(i18n): Use t tagged template: value = t`Added`;
            value = "Added";
        }
        const prefixName = code
            .substr(code.indexOf("_") + 1, code.length)
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => {
                return word[0].toUpperCase() + word.substring(1);
            })
            .join(" ");
        const prefixNameCapped = isPLCVersion && (
            <span className="sticky-configurator__option-prefix">{`${prefixName}: `}</span>
        );
        return (
            <div className="sticky-configurator__option" key={code}>
                {prefixNameCapped}
                {value}
            </div>
        );
    });

    // Build title
    // TODO: Using the product attribute from Oscar for the title
    // and adding replace('TEMPUR-', '') so the title reads someting like "ProAdapt" instead of "TEMPUR-ProAdapt"
    // There might be a better way but not feagured out yet.
    const richTextTitle = rootProduct.attributes.title_richtext;
    const displayTitle = richTextTitle
        ? `${richTextTitle.value?.toString()}`.replace("TEMPUR-", "")
        : rootProduct.title;
    const title: React.JSX.Element = richTextTitle ? (
        <RichText
            className={`${styles.title} sticky-configurator__title`}
            html={displayTitle}
        />
    ) : (
        <div className={`${styles.title} sticky-configurator__title`}>
            {displayTitle}
        </div>
    );
    const copyClasses = classNames({
        "sticky-configurator__copy": true,
        "sticky-configurator__copy--expanded": areOptionsExpanded,
    });
    const expandButtonClasses = classNames({
        "sticky-configurator__expand-button": true,
        "sticky-configurator__expand-button--expanded": areOptionsExpanded,
    });
    const copy: React.JSX.Element = isPLCVersion ? (
        <div>
            <div className={copyClasses}>
                <div className="sticky-configurator__options">
                    {categoryOptions}
                    {productOptions}
                </div>
            </div>
            <div className={expandButtonClasses}>
                <button onClick={onExpandOptions}>
                    <SVG src={iconArrowDownBlueCircle} />
                </button>
            </div>
        </div>
    ) : (
        <div className={copyClasses}>
            <div className="sticky-configurator__options">
                {categoryOptions}
                {productOptions}
            </div>
        </div>
    );

    // Build content
    return (
        <div
            className={`${styles.copyContainer} sticky-configurator__copy-container`}
        >
            <div>
                <span>
                    {title}
                    {!isPLCVersion && rootProduct.num_reviews > 0 && (
                        <RatingGraphic
                            cardClass="sticky-configurator"
                            cardSize="large"
                            numReviews={rootProduct.num_reviews}
                            rating={rootProduct.rating}
                        />
                    )}
                </span>
                <span>
                    {buildPrice(
                        price,
                        upgradedVariant,
                        financingLink,
                        strikeThroughMSRP,
                        actualPriceStyle,
                        overrideFinancingCopy,
                    )}
                </span>
            </div>
            {copy}
        </div>
    );
};

const buildErrorModal = (
    addToBasketErrorOpen: boolean,
    addToBasketErrorReason: string,
    onClose: () => void,
): React.JSX.Element => {
    const reason = addToBasketErrorReason;
    return (
        <ErrorModal
            name="configurator"
            isOpen={addToBasketErrorOpen}
            onRequestClose={(e) => onCloseErrorModal(onClose, e)}
        >
            <p>{t`Product could not be added to cart (${reason}).`}</p>
            <p>{t`Please adjust your selection and try again.`}</p>
        </ErrorModal>
    );
};

const buildAddToBasketButton = (
    addToBasketButtonText: string,
    upgradedVariant: IProduct | null,
    addToBasketCooldownActive: boolean,
    variantIsAvailableToBuy: boolean,
    rootProduct: IProduct | null,
    onAddToBasket: () => void,
): React.JSX.Element => {
    const btnText = addToBasketButtonText;
    const isPreorder = upgradedVariant && isPreorderProduct(upgradedVariant);
    const btnClasses = classNames({
        "button": true,
        "button--secondary": isPreorder,
        "button--primary-congress-blue": !isPreorder,
        "sticky-configurator__add-button": true,
    });
    return (
        <div
            className={`${styles.ctaContainer} sticky-configurator__cta-container`}
        >
            <button
                className={btnClasses}
                disabled={addToBasketCooldownActive || !variantIsAvailableToBuy}
                onClick={(e) =>
                    onAddToBasketClick(rootProduct, onAddToBasket, e)
                }
            >
                {btnText}
            </button>
        </div>
    );
};

const buildCta = (
    variantIsAvailableToBuy: boolean,
    addToBasketButtonText: string,
    upgradedVariant: IProduct | null,
    addToBasketCooldownActive: boolean,
    rootProduct: IProduct | null,
    onAddToBasket: () => void,
    onEditClick: () => void,
): React.JSX.Element => {
    return (
        <div className="sticky-configurator__cta">
            {buildAddToBasketButton(
                addToBasketButtonText,
                upgradedVariant,
                addToBasketCooldownActive,
                variantIsAvailableToBuy,
                rootProduct,
                onAddToBasket,
            )}
            {/* TODO(i18n): Use t tagged template: {t`Change Selection`} */}
            <button onClick={onEditClick}>Change Selection</button>
        </div>
    );
};

export const StickyConfigurator = (props: {
    isPLCVersion: boolean;

    // Basic Data
    financingLink?: IWebPageURL | null;
    rootProduct: IProduct | null;
    concreteBundles: readonly IConcreteBundle[];
    baseVariant: IProduct | null;
    upgradedVariant: IProduct | null;
    price: IAPIPrice | null;

    // Option selector state
    optionValues: IOptionValues;
    quantity: number;

    // Add to basket button
    addToBasketCooldownActive: boolean;
    addToBasketButtonText: string;
    onAddToBasket: () => void;

    // Add to basket error modal
    addToBasketErrorOpen: boolean;
    addToBasketErrorReason: string;
    onCloseErrorModal: () => void;

    // Misc UI options
    isVisible: boolean;
    onEditClick: () => void;
    strikeThroughMSRP: boolean;
    actualPriceStyle: string;
    overrideFinancingCopy: string;
    selectedCategories?: IProductCategory[];

    setStickyConfiguratorHeight: (height: number) => void;
    stickyElemHeight: number;

    children?: React.ReactNode;
}): React.ReactNode => {
    const [areOptionsExpanded, setAreOptionsExpanded] = useState(false);
    const configuratorElementRef = useRef<HTMLDivElement | null>(null);

    const handleExpandOptions = (): void => {
        setAreOptionsExpanded((prev) => !prev);
    };

    useEffect(() => {
        if (!configuratorElementRef.current) {
            return;
        }
        props.setStickyConfiguratorHeight(
            configuratorElementRef.current.clientHeight,
        );
    }, []);

    const variantIsAvailableToBuy = !!(
        props.baseVariant && props.baseVariant.availability.is_available_to_buy
    );
    const stickyElemMinHeight = 300;
    const configuratorStyle = {
        top: !props.isVisible
            ? `-${props.stickyElemHeight + stickyElemMinHeight}px`
            : 0,
    };

    return (
        <div
            className={`${styles.root} sticky-configurator`}
            style={configuratorStyle}
            ref={configuratorElementRef}
        >
            <div className="sticky-configurator__content">
                <div className="sticky-configurator__info-container">
                    {buildCopy(
                        props.rootProduct,
                        props.selectedCategories,
                        props.optionValues,
                        props.isPLCVersion,
                        areOptionsExpanded,
                        handleExpandOptions,
                        props.price,
                        props.upgradedVariant,
                        props.financingLink,
                        props.strikeThroughMSRP,
                        props.actualPriceStyle,
                        props.overrideFinancingCopy,
                    )}
                </div>
                {buildCta(
                    variantIsAvailableToBuy,
                    props.addToBasketButtonText,
                    props.upgradedVariant,
                    props.addToBasketCooldownActive,
                    props.rootProduct,
                    props.onAddToBasket,
                    props.onEditClick,
                )}
                {props.children}
                {buildErrorModal(
                    props.addToBasketErrorOpen,
                    props.addToBasketErrorReason,
                    props.onCloseErrorModal,
                )}
            </div>
        </div>
    );
};
